import React, { createContext, useContext, useEffect, useState } from "react";
import { signup, googleAuth, checkAuth, logout } from "../service/auth";
import axiosInstance from "../configuration/api";

interface AuthContextType {
  isAuthenticated: boolean;
  user: any;
  loading: boolean;
  initialized: boolean;
  login: (token: string, user: any) => Promise<void>;
  logout: () => Promise<void>;
  signup: (userData: any) => Promise<void>;
  googleSignIn: () => void;
}

const AuthContext = createContext<AuthContextType>(null!);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null,
    loading: true,
    initialized: false,
  });

  const verifyAuthState = async () => {
    try {
      const authData = localStorage.getItem("authData");
      if (!authData) return { isValid: false, user: null };

      const parsedData = JSON.parse(authData);
      if (!parsedData.token) return { isValid: false, user: null };

      // Verify with backend
      if (parsedData.user?.role === "SUPER_ADMIN") {
        return { isValid: true, user: parsedData.user };
      }
      const response = await axiosInstance.get("/api/v1/user/protected", {
        _skipAuthRefresh: true,
        headers: {
          Authorization: `Bearer ${parsedData.token}`,
        },
      });

      return { isValid: response.status === 200, user: parsedData.user };
    } catch (error) {
      console.error("Auth verification failed:", error);
      localStorage.removeItem("authData");
      return { isValid: false, user: null };
    }
  };

  const initializeAuth = async () => {
    const { isValid, user } = await verifyAuthState();
    setAuthState({
      isAuthenticated: isValid,
      user: isValid ? user : null,
      loading: false,
      initialized: true,
    });
  };

  useEffect(() => {
    initializeAuth();
  }, []);

  const handleLogin = async (token: string, user: any) => {
    const authData = { token, user };
    localStorage.setItem("authData", JSON.stringify(authData));
    setAuthState({
      isAuthenticated: true,
      user,
      loading: false,
      initialized: true,
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      localStorage.removeItem("authData");
      setAuthState({
        isAuthenticated: false,
        user: null,
        loading: false,
        initialized: true,
      });
    }
  };

  const handleSignup = async (userData: any) => {
    const { user, token } = await signup(userData);
    await handleLogin(token, user);
  };

  const handleGoogleSignIn = () => {
    localStorage.setItem("preAuthPath", window.location.pathname);
    googleAuth();
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login: handleLogin,
        logout: handleLogout,
        signup: handleSignup,
        googleSignIn: handleGoogleSignIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
// Add this to your auth context
setInterval(async () => {
  const isAuthenticated = await checkAuth();
  if (!isAuthenticated) {
    await logout();
  }
}, 30 * 60 * 1000); // Check every 30 minutes
export function useAuth() {
  return useContext(AuthContext);
}
